/**
 * Cross-customer portfolio dashboard for MSSP administrators and operators.
 * Loads summaries through msspApi, derives lightweight display totals, filters
 * locally, and navigates by immutable LICENSE id.
 */
import { AlertTriangle, ArrowRight, RefreshCw, Search, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { listCustomers } from "./msspApi";

const formatDate = (value) => value ? new Date(value).toLocaleDateString() : "Not scheduled";
const titleCase = (value) => value ? value.replaceAll("_", " ").replace(/^./, (letter) => letter.toUpperCase()) : "Not assigned";

export default function MsspCustomers() {
  const [customers, setCustomers] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try { setCustomers(await listCustomers()); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    // Filtering is intentionally client-side for the MVP-size result set. Move
    // search/pagination to the API when customer counts make this inefficient.
    const needle = query.trim().toLowerCase();
    if (!needle) return customers;
    return customers.filter((customer) =>
      [customer.companyName, customer.adminName, customer.adminEmail, customer.primaryServiceName, ...(customer.serviceModes || [])]
        .some((value) => String(value || "").toLowerCase().includes(needle))
    );
  }, [customers, query]);

  // These are presentation totals only; authoritative counts still come from
  // the customer records returned by the backend.
  const activeCount = customers.filter((customer) => customer.licenseActive).length;
  const dueCount = customers.filter((customer) => customer.nextReportAt && new Date(customer.nextReportAt) < new Date("2026-09-08T00:00:00Z")).length;
  const findingCount = customers.reduce((sum, customer) => sum + customer.openFindings, 0);

  return (
    <section className="mssp-page">
      <div className="mssp-page-heading">
        <div><p className="mssp-eyebrow">Portfolio</p><h1>Managed customers</h1><p>Review licenses, service coverage, findings, and upcoming reports.</p></div>
        <button className="mssp-button secondary" onClick={load} disabled={loading}><RefreshCw size={16} /> Refresh</button>
      </div>

      <div className="mssp-stat-grid">
        <div className="mssp-stat"><span>Managed customers</span><strong>{customers.length}</strong><small>{activeCount} active licenses</small></div>
        <div className="mssp-stat"><span>Reports due soon</span><strong>{dueCount}</strong><small>Next seven days</small></div>
        <div className="mssp-stat"><span>Open findings</span><strong>{findingCount}</strong><small>Across all customers</small></div>
      </div>

      <div className="mssp-panel">
        <div className="mssp-toolbar">
          <label className="mssp-search"><Search size={17} /><span className="sr-only">Search customers</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search company, admin, service, or mode" /></label>
          <span>{filtered.length} customer{filtered.length === 1 ? "" : "s"}</span>
        </div>
        {error && <div className="mssp-alert error" role="alert">{error}</div>}
        <div className="mssp-table-wrap">
          <table className="mssp-table">
            <thead><tr><th>Customer</th><th>License</th><th>Service</th><th>Users</th><th>Latest score</th><th>Findings</th><th>Next report</th><th><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan="8" className="mssp-empty">Loading customers…</td></tr> : filtered.map((customer) => (
                <tr key={customer.licenseId}>
                  <td><strong>{customer.companyName}</strong><small>{customer.adminEmail}</small></td>
                  <td><span className={`mssp-status ${customer.licenseActive ? "good" : "bad"}`}>{customer.licenseActive ? "Active" : "Expired"}</span><small>Ends {formatDate(customer.endDate)}</small></td>
                  <td><span className="mssp-tier">{customer.serviceCount || 0} service{customer.serviceCount === 1 ? "" : "s"}</span><small>{customer.serviceModes?.map(titleCase).join(" + ") || "Not assigned"}</small></td>
                  <td><span className="mssp-icon-value"><Users size={15} />{customer.activeUsers}/{customer.maxUsers}</span></td>
                  <td><strong>{customer.latestScore}%</strong></td>
                  <td><span className="mssp-icon-value"><AlertTriangle size={15} />{customer.openFindings}</span></td>
                  <td>{formatDate(customer.nextReportAt)}</td>
                  <td><Link className="mssp-icon-link" to={`/mssp/customers/${customer.licenseId}`} aria-label={`Open ${customer.companyName}`}><ArrowRight size={18} /></Link></td>
                </tr>
              ))}
              {!loading && filtered.length === 0 && <tr><td colSpan="8" className="mssp-empty">No customers match the current search.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
